/**
 * FLIK activity-type visual system.
 *
 * The backend's `activityType` is an admin-defined slug derived from a free-form
 * name (e.g. "senderismo", "viasFerratas", "escalada"). It is therefore not a
 * fixed enum, so we resolve it with a keyword matcher over the normalised
 * slug/name rather than an exact lookup.
 *
 * Each canonical activity carries the icon path and pin colour from the FLIK
 * design system ("ICONOS DE ACTIVIDAD" + "PIN DE COTA"):
 *   - Icons are a single <path> on a 24×24 grid, stroke 1.75, butt caps, miter
 *     joins, no fill, no curves — drawn in roca-900 over the flat pin colour.
 *   - One colour per activity; brand gold is reserved for the selected pin and
 *     is never an activity colour.
 * When a type matches nothing, it falls back to the FLIK mark on a cream pin.
 */

// roca-900 — the stroke colour of every pin icon (>4.5:1 over any pin colour).
export const PIN_ICON_STROKE = '#101C15';

// Canonical activities, in the design's order. `match` is the list of
// accent-free, lowercased substrings that map a slug/name onto this entry.
export const ACTIVITY_TYPES = [
  {
    key: 'senderismo',
    label: 'Senderismo',
    color: '#9BD46A',
    match: ['senderismo', 'senderis', 'trekking', 'hiking', 'ruta', 'marcha'],
    path: 'M5 20 L5 12 L9 12 L9 15 L14 15 L14 20 Z M5 16.5 L14 16.5 M5 18.5 L14 18.5 M19 4 L19 20 M19 8 L22 6',
  },
  {
    key: 'ferrata',
    label: 'Vías ferratas',
    color: '#E9A13B',
    match: ['ferrata', 'ferratas', 'viaferrata', 'viasferratas'],
    path: 'M9 2 L9 22 M17 2 L17 22 M9 6 L17 6 M9 11 L17 11 M9 16 L17 16 M3 3 L3 21 M3 9 L9 9',
  },
  {
    key: 'escalada',
    label: 'Escalada',
    color: '#E0503A',
    match: ['escalada', 'escalar', 'climbing', 'boulder'],
    path: 'M20 2 L20 22 M14 6 L20 6 M16 12 L20 12 M13 18 L20 18 M4 2 L4 9 L8 13 L4 17 L4 22',
  },
  {
    key: 'barranquismo',
    label: 'Barranquismo',
    color: '#6FA8C7',
    match: ['barranquismo', 'barranco', 'barrancos', 'barranc', 'canyoning'],
    path: 'M5 2 L5 12 L9 22 M19 2 L19 12 L15 22 M12 2 L12 10 M10 10 L14 10 L14 14 L10 14 Z M8 18 L16 18',
  },
  {
    key: 'espeleologia',
    label: 'Espeleología',
    color: '#C9C3B0',
    match: ['espeleologia', 'espeleo', 'cueva', 'cuevas', 'caving', 'speleo'],
    path: 'M2 21 L2 12 L9 5 L15 5 L22 12 L22 21 M2 21 L22 21 M10 5 L12 11 M14 5 L16 10',
  },
  {
    key: 'ciclismo',
    label: 'Ciclismo',
    color: '#58B0A8',
    match: ['ciclismo', 'ciclista', 'bici', 'bike', 'btt', 'mtb', 'cycling'],
    path: 'M6 12 L10 16 L6 20 L2 16 Z M18 12 L22 16 L18 20 L14 16 Z M6 16 L10 9 L18 16 M10 9 L17 9 M17 9 L19 6',
  },
];

// FLIK fallback for a type with no defined icon: the brand symbol on a cream
// pin, with the symbol in roca-900 (recoloured via CSS mask) — same dark-icon-
// on-light-pin scheme as every other type. Cream isn't used by any activity, so
// the fallback stays distinct.
export const FLIK_FALLBACK = {
  key: 'flik',
  label: 'FLIK',
  color: '#F4F1E8',
};

/**
 * Which stats each activity type shows on its list item and route card, in
 * display order. Backend field keys are admin-defined (derived from a label), so
 * every stat carries a list of candidate `keys` — the first one present on the
 * activity's `values` wins. This keeps the cards working whether the fields were
 * seeded with English keys (`distance`, `gain`) or Spanish camelCase ones
 * (`distancia`, `desnivel`).
 *
 * `kind` selects a special renderer:
 *   - 'mide'      → the four MIDE levels joined by '-' (e.g. "2-3-2-4").
 *   - 'bool'      → a check when true, an X when false.
 *   - 'routeType' → an MTB / road icon plus its label.
 * Everything else renders as the raw value under `unit`.
 */
export const ACTIVITY_CARD_STATS = {
  senderismo: [
    { keys: ['distance', 'distancia'], unit: 'km' },
    { keys: ['gain', 'desnivel', 'desnivelPositivo'], unit: 'D+' },
    { keys: ['time', 'tiempo', 'duracion'], unit: 'horas' },
    { keys: ['mide'], unit: 'MIDE', kind: 'mide' },
  ],
  ferrata: [
    { keys: ['ferrataDifficulty', 'difficulty', 'dificultad', 'grado'], unit: 'Dificultad' },
  ],
  barranquismo: [
    { keys: ['verticalDifficulty', 'verticalGrade', 'dificultadVertical'], unit: 'Vertical' },
    { keys: ['aquaticDifficulty', 'aquaticGrade', 'dificultadAcuatica'], unit: 'Acuática' },
    { keys: ['commitment', 'compromiso'], unit: 'Compromiso' },
  ],
  escalada: [
    { keys: ['uiaaGrade'], unit: 'UIAA' },
    { keys: ['frenchGrade'], unit: 'Francés' },
    { keys: ['lengthMeters', 'length', 'dimension', 'longitud'], unit: 'm' },
    { keys: ['pitches', 'largos'], unit: 'Largos' },
    { keys: ['equipped', 'equipada', 'equipado'], unit: 'Equipada', kind: 'bool' },
  ],
  espeleologia: [
    { keys: ['verticalDrop', 'desnivel'], unit: 'm' },
    { keys: ['numberOfWells', 'pits', 'pozos', 'numeroPozos'], unit: 'Pozos' },
    { keys: ['cavingDifficulty', 'difficulty', 'dificultad', 'dificultadCavidad'], unit: 'Dificultad' },
  ],
  ciclismo: [
    { keys: ['distance', 'distancia'], unit: 'km' },
    { keys: ['gain', 'desnivel', 'desnivelPositivo'], unit: 'D+' },
    { keys: ['time', 'tiempo', 'duracion'], unit: 'horas' },
    { keys: ['routeType', 'tipoRuta', 'tipo'], unit: 'Ruta', kind: 'routeType' },
  ],
};

// Sub-field keys that make up a MIDE rating when it isn't stored as one string.
const MIDE_PART_KEYS = [
  ['mideMedio', 'medio'],
  ['mideItinerario', 'itinerario'],
  ['mideDesplazamiento', 'desplazamiento'],
  ['mideEsfuerzo', 'esfuerzo'],
];

/** First non-empty value among `keys` on the activity, or undefined. */
function pick(values, keys) {
  for (const k of keys) {
    const v = values?.[k];
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return undefined;
}

/**
 * Builds a MIDE string ("Medio-Itinerario-Desplazamiento-Esfuerzo"). Accepts a
 * single `mide` field already in that form, or the four levels as separate
 * fields. Always renders the four slots in order, using '?' for any missing
 * level (e.g. "2-?-2-4"). Returns undefined only when no level is set at all.
 */
function readMide(values) {
  const single = pick(values, ['mide']);
  if (single !== undefined) return String(single).replace(/[\s/,]+/g, '-');
  const parts = MIDE_PART_KEYS.map((keys) => pick(values, keys));
  if (parts.every((v) => v === undefined)) return undefined;
  return parts.map((v) => (v === undefined ? '?' : v)).join('-');
}

/** True when a value reads as an affirmative boolean. */
function isTruthy(v) {
  if (typeof v === 'boolean') return v;
  const s = String(v).trim().toLowerCase();
  return s === 'true' || s === 'sí' || s === 'si' || s === '1' || s === 'yes';
}

// Route-type values that map to a mountain-bike vs a road-bike icon.
const MTB_MATCH = ['mtb', 'btt', 'montaña', 'montana', 'mountain'];

/** Classifies a ciclismo route value as 'mtb' or 'road'. */
export function classifyRouteType(value) {
  const s = String(value ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
  return MTB_MATCH.some((m) => s.includes(m)) ? 'mtb' : 'road';
}

/**
 * Resolves the display stats for one activity: an ordered list of
 * `{ unit, kind, value, present }`. `value` is already formatted for its kind
 * (a MIDE string, a boolean, or a raw scalar). `present` is false when no
 * candidate key held a value, so callers can show a placeholder.
 */
export function getActivityStats(activity) {
  const type = resolveActivityType(activity?.activityType);
  const spec = ACTIVITY_CARD_STATS[type.key];
  if (!spec) return [];
  const values = activity?.values || {};
  return spec.map((stat) => {
    let value;
    if (stat.kind === 'mide') value = readMide(values);
    else value = pick(values, stat.keys);
    const present = value !== undefined;
    if (stat.kind === 'bool') value = isTruthy(value);
    return { unit: stat.unit, kind: stat.kind, value, present };
  });
}

/** Lowercases and strips accents so slugs and names match regardless of form. */
function normalize(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]/gi, '')
    .toLowerCase();
}

/**
 * Resolves an `activityType` slug (or name) to its visual definition, falling
 * back to the FLIK mark when nothing matches. Always returns a usable entry.
 */
export function resolveActivityType(activityType) {
  const norm = normalize(activityType);
  if (norm) {
    const hit = ACTIVITY_TYPES.find((t) => t.match.some((m) => norm.includes(m)));
    if (hit) return hit;
  }
  return FLIK_FALLBACK;
}
